import React, { useEffect, useRef, useState } from 'react';
import { Building2, Loader2 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { api } from '../api';
import { inputCls } from '../ui';

export default function CompanySuggest({ value, onChange, country, onPick }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const skip = useRef(false);
  const focused = useRef(false);

  useEffect(() => {
    if (skip.current) { skip.current = false; return undefined; }
    const q = value.trim();
    if (q.length < 3) { setItems([]); setOpen(false); return undefined; }
    let alive = true;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/cs/suggest/companies', { params: { q, countries: country } });
        if (alive) { setItems(data); setOpen(focused.current); setActive(-1); }
      } catch { if (alive) setItems([]); } finally { if (alive) setLoading(false); }
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [value, country]);

  const pick = (c) => { skip.current = true; setOpen(false); onPick(c); };
  const onKey = (e) => {
    focused.current = e.key !== 'Enter' || active >= 0;
    if (!focused.current) { setOpen(false); return; }
    if (!open || !items.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === 'Enter' && active >= 0) { e.preventDefault(); pick(items[active]); }
    if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div className="relative">
      <Input value={value} onChange={onChange} onKeyDown={onKey} onFocus={() => { focused.current = true; if (items.length) setOpen(true); }} onBlur={() => { focused.current = false; setTimeout(() => setOpen(false), 150); }}
        className={inputCls} autoComplete="off" placeholder="Inizia a scrivere…" data-testid="search-name" />
      {loading && <Loader2 size={14} className="animate-spin text-[#D4AF37] absolute right-3 top-1/2 -translate-y-1/2" />}
      {open && (
        <div className="absolute z-30 left-0 right-0 lg:right-auto lg:w-[520px] mt-1 bg-[#0B0D10] border border-[#1F242C] shadow-2xl max-h-[380px] overflow-y-auto" data-testid="search-suggest-list">
          {items.length === 0 ? <p className="px-4 py-3 text-sm text-[#5A5F67]" data-testid="search-suggest-empty">Nessuna azienda suggerita</p> : items.map((c, i) => (
            <button type="button" key={c.id} onMouseDown={(e) => { e.preventDefault(); pick(c); }} onMouseEnter={() => setActive(i)}
              className={`w-full text-left px-4 py-3 flex items-start gap-3 border-b border-[#1F242C] last:border-0 transition-colors ${i === active ? 'bg-[#D4AF37]/[0.08]' : ''}`} data-testid={`search-suggest-${i}`}>
              <Building2 size={15} className="text-[#D4AF37] mt-0.5 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-[#F2F2F2] truncate">{c.name}</span>
                <span className="block text-xs font-mono text-[#5A5F67] truncate">{[c.city, c.province, c.vatNo && `P.IVA ${c.vatNo}`, c.status === 'active' ? 'Attiva' : (c.status ? 'Non attiva' : '')].filter(Boolean).join(' · ')}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
